/**
 * The one place that knows which renderer draws the world. WebGL2 is the baseline; switching to
 * WebGPU later changes this module alone.
 */
import * as THREE from 'three';

import type { Look } from '../style/look';

export interface WorldRenderer {
  readonly gl: THREE.WebGLRenderer;
  /** Fit the canvas to the window; returns the size in CSS pixels. */
  resize(): { width: number; height: number };
  render(scene: THREE.Scene, camera: THREE.Camera): void;
  /** Draw calls and triangles of the last frame. */
  stats(): { calls: number; triangles: number };
  /**
   * Draw in a look: the classic one with its shadow map and no tone curve, the stylized one without
   * shadows and with a gentle tone curve; at most `pixelRatio` device pixels per CSS pixel.
   */
  setLook(look: Look, pixelRatio: number): void;
  dispose(): void;
}

export function createRenderer(canvas: HTMLCanvasElement): WorldRenderer {
  const gl = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    powerPreference: 'high-performance',
  });
  gl.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
  gl.shadowMap.enabled = true;
  gl.shadowMap.type = THREE.PCFShadowMap;
  gl.localClippingEnabled = false;
  gl.setClearColor(0x000000, 0);

  return {
    gl,
    resize() {
      const width = window.innerWidth;
      const height = window.innerHeight;
      gl.setSize(width, height, false);
      return { width, height };
    },
    render(scene, camera) {
      gl.render(scene, camera);
    },
    stats: () => ({ calls: gl.info.render.calls, triangles: gl.info.render.triangles }),
    setLook(look, pixelRatio) {
      gl.shadowMap.enabled = look === 'classic';
      gl.toneMapping = look === 'stylized' ? THREE.NeutralToneMapping : THREE.NoToneMapping;
      gl.setPixelRatio(Math.min(pixelRatio, window.devicePixelRatio || 1));
    },
    dispose() {
      gl.dispose();
    },
  };
}
